import { useState } from "react";
import { getAuth } from "firebase/auth";
import { FirebaseError } from "firebase-admin";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useEmailLogin } from "hooks";

interface Options {
  /**
   * Called when the current session can't be used and there is nothing left to
   * do in-place (e.g. no email on the account to send a recovery link to).
   */
  onClose: () => void;
  /**
   * Called when the session is too old and the user must re-authenticate from
   * the signup flow (shows the ExpiredSessionModal).
   */
  onExpiredSession: () => void;
}

/**
 * Centralizes error handling for the MFA enrollment calls
 * (`multiFactor(user).getSession()` / `.enroll()`), which are duplicated across
 * the TOTP and Phone setup flows.
 *
 * The notable case is `auth/unsupported-first-factor`: Firebase refuses MFA
 * enrollment when the user's current session was established with a first
 * factor it doesn't support for MFA — most commonly a custom-token session
 * handed off from the signup app (see ENG-2029). The account itself may well
 * have a password and a verified email, but the *live session* isn't eligible.
 *
 * To recover, we surface a prompt (`recoveryNeeded`) so the user can trigger an
 * email login link via `sendRecoveryLink`. Following that link establishes an
 * email-link session, which Firebase does allow for MFA enrollment, and the
 * `redirect` returns them to this setup screen.
 *
 * Stale-session errors (`auth/requires-recent-login` / `auth/user-token-expired`)
 * take the same email-link recovery path when the user is enrolling outside the
 * onboarding flow. Within onboarding they instead re-authenticate from scratch
 * via `onExpiredSession` (the ExpiredSessionModal).
 */
export const useMfaEnrollmentErrorHandler = ({
  onClose,
  onExpiredSession,
}: Options) => {
  const location = useLocation();
  const { login, isSendingEmail } = useEmailLogin();
  const [recoveryNeeded, setRecoveryNeeded] = useState(false);
  const [recoveryLinkSent, setRecoveryLinkSent] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);

  const sendRecoveryLink = async () => {
    // Avoid re-sending (the login endpoint is rate limited).
    if (recoveryLinkSent || !recoveryEmail) return;

    // Return the user to this same setup screen after they follow the link.
    await login(recoveryEmail, location.pathname);
    setRecoveryLinkSent(true);
  };

  const startSessionRecovery = () => {
    const email = getAuth().currentUser?.email;
    if (!email) {
      onClose();
      return;
    }
    setRecoveryEmail(email);
    setRecoveryNeeded(true);
  };

  const handleEnrollmentError = async (error: FirebaseError) => {
    switch (error.code) {
      case "auth/requires-recent-login":
      case "auth/user-token-expired":
      case "auth/unsupported-first-factor": {
        // In the onboarding flow the user must re-authenticate from scratch;
        // elsewhere we can recover the session in-place via an email link.
        if (location.pathname === "/onboard") {
          onExpiredSession();
        } else {
          startSessionRecovery();
        }
        break;
      }
      default: {
        console.error(error);
        toast.error("There was an error");
      }
    }
  };

  return {
    handleEnrollmentError,
    recoveryNeeded,
    recoveryLinkSent,
    recoveryEmail,
    isSendingEmail,
    sendRecoveryLink,
  };
};
